import { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { showToast } from '../utils/toast';
import { t } from '../i18n/index.js';
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';

export const STAFF_ROLES = {
  ADMIN: {
    id: 'admin',
    aliases: ['first_coach', 'owner', 'admin'],
    label: 'Primer Entrenador (Admin)',
    badge: 'Primer Entrenador',
    color: '#D4A843',
    textColor: '#FFFFFF',
    description: 'Control total: gestión del equipo, miembros, partidos, sesiones, salud y táctica.'
  },
  COACH: {
    id: 'coach',
    aliases: ['second_coach', 'coach'],
    label: 'Segundo Entrenador',
    badge: 'Segundo Entrenador',
    color: '#10B981',
    textColor: '#FFFFFF',
    description: 'Edición de partidos, alineaciones, sesiones, pizarra, planning y asistencia.'
  },
  FITNESS_COACH: {
    id: 'fitness_coach',
    aliases: ['physical_trainer', 'preparador_fisico', 'pf', 'fitness', 'preparador'],
    label: 'Preparador Físico',
    badge: 'Preparador Físico',
    color: '#F59E0B',
    textColor: '#FFFFFF',
    description: 'Control de preparación física, cargas de entrenamiento, pruebas físicas y estado atlético.'
  },
  ASSISTANT: {
    id: 'assistant',
    aliases: ['assistant_coach', 'assistant'],
    label: 'Ayudante / 3er Entrenador',
    badge: 'Ayudante',
    color: '#3B82F6',
    textColor: '#FFFFFF',
    description: 'Registro de Live Stats, control de asistencia y apoyo en sesiones.'
  },
  PHYSIO: {
    id: 'physio',
    aliases: ['medical', 'physio'],
    label: 'Fisioterapeuta / Médico',
    badge: 'Fisioterapeuta',
    color: '#EF4444',
    textColor: '#FFFFFF',
    description: 'Acceso y edición de historial médico, lesiones, tests físicos/wellness y asistencia.'
  },
  ANALYST: {
    id: 'analyst',
    aliases: ['scout', 'analyst'],
    label: 'Analista Táctico',
    badge: 'Analista',
    color: '#1B3A2D',
    textColor: '#FFFFFF',
    description: 'Registro y análisis de Live Stats en tiempo real, informes y analíticas.'
  },
  PLAYER: {
    id: 'player',
    aliases: ['jugador', 'player'],
    label: 'Jugador',
    badge: 'Jugador',
    color: '#94A3B8',
    textColor: '#FFFFFF',
    description: 'Acceso de solo lectura a su ficha individual, sesiones y calendario.'
  }
};

export const normalizeRole = (role) => {
  if (!role) return 'admin';
  const r = role.toLowerCase().trim();
  for (const roleKey of Object.keys(STAFF_ROLES)) {
    const info = STAFF_ROLES[roleKey];
    if (info.id === r || info.aliases.includes(r)) {
      return info.id;
    }
  }
  return 'coach';
};

export const getRoleInfo = (role) => {
  const norm = normalizeRole(role);
  const found = Object.values(STAFF_ROLES).find(r => r.id === norm);
  return found || STAFF_ROLES.ADMIN;
};

export const useTeamMembers = (teamIdOverride = null) => {
  const { user, userProfile, activeTeamId, getTeamPath, teams } = useAuth();
  const targetTeamId = teamIdOverride || activeTeamId;
  const currentTeam = useMemo(() => teams.find(t => t.id === targetTeamId) || null, [teams, targetTeamId]);

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitations, setInvitations] = useState([]);

  // 1. Escuchar en tiempo real los miembros del equipo
  useEffect(() => {
    if (!user || !targetTeamId) {
      setMembers([]);
      setLoading(false);
      return;
    }

    const teamPath = getTeamPath(targetTeamId);
    if (!teamPath) {
      setLoading(false);
      return;
    }

    // Escuchar subcolección 'members' dentro del equipo
    const membersRef = collection(db, `${teamPath}/members`);
    const unsub = onSnapshot(membersRef, async (snapshot) => {
      let rawList = snapshot.docs.map(d => ({
        id: d.id,
        uid: d.id,
        ...d.data(),
        normalizedRole: normalizeRole(d.data().role)
      }));

      // Nombre canónico del usuario actual desde su perfil de entrenador en Firestore
      const selfCanonicalName = userProfile?.nombre || userProfile?.displayName || userProfile?.name || user.displayName || currentTeam?.ownerName || 'Entrenador';

      // Si la subcolección está vacía o falta el usuario actual
      const selfExists = rawList.some(m => m.uid === user.uid || m.id === user.uid);
      if (!selfExists && user) {
        const selfMember = {
          id: user.uid,
          uid: user.uid,
          email: user.email || '',
          displayName: selfCanonicalName,
          name: selfCanonicalName,
          role: currentTeam?.ownerUid === user.uid ? 'admin' : (user.role || 'coach'),
          normalizedRole: normalizeRole(currentTeam?.ownerUid === user.uid ? 'admin' : (user.role || 'coach')),
          joinedAt: new Date().toISOString()
        };
        rawList = [selfMember, ...rawList];
        // Auto-persistir para sincronización futura
        try {
          await setDoc(doc(db, `${teamPath}/members`, user.uid), selfMember, { merge: true });
        } catch (_) {}
      }

      // Enriquecer cada miembro con el nombre canónico real de su perfil de entrenador
      const enrichedList = await Promise.all(rawList.map(async (m) => {
        let realName = m.displayName || m.name || '';
        const isSelf = user && (m.uid === user.uid || m.id === user.uid);

        if (isSelf) {
          if (selfCanonicalName && selfCanonicalName !== 'Entrenador') {
            realName = selfCanonicalName;
            // Sincronizar en la subcolección members si difiere de lo que había guardado
            if (m.displayName !== selfCanonicalName || m.name !== selfCanonicalName) {
              try {
                setDoc(doc(db, `${teamPath}/members`, user.uid), {
                  displayName: selfCanonicalName,
                  name: selfCanonicalName
                }, { merge: true });
              } catch (_) {}
            }
          }
        } else if (m.uid) {
          // Consultar perfil users/{m.uid} para obtener el nombre canónico del entrenador
          try {
            const uSnap = await getDoc(doc(db, 'users', m.uid));
            if (uSnap.exists()) {
              const uData = uSnap.data();
              const fetchedName = uData.nombre || uData.displayName || uData.name;
              if (fetchedName) {
                realName = fetchedName;
              }
            }
          } catch (_) {}
        }

        return {
          ...m,
          displayName: realName || m.displayName || m.email?.split('@')[0] || 'Entrenador',
          name: realName || m.name || m.displayName || m.email?.split('@')[0] || 'Entrenador'
        };
      }));

      setMembers(enrichedList);
      setLoading(false);
    }, (err) => {
      console.warn('[useTeamMembers] Error al escuchar miembros:', err);
      // Fallback con el usuario actual si hay error de permisos
      if (user) {
        const selfName = userProfile?.nombre || userProfile?.displayName || userProfile?.name || user.displayName || 'Entrenador';
        setMembers([{
          id: user.uid,
          uid: user.uid,
          email: user.email,
          displayName: selfName,
          name: selfName,
          role: 'admin',
          normalizedRole: 'admin'
        }]);
      }
      setLoading(false);
    });

    return () => unsub();
  }, [user, userProfile, targetTeamId, getTeamPath, currentTeam]);

  // 2. Escuchar invitaciones pendientes del equipo
  useEffect(() => {
    if (!user || !targetTeamId) {
      setInvitations([]);
      return;
    }

    const teamPath = getTeamPath(targetTeamId);
    if (!teamPath) return;

    const invRef = collection(db, `${teamPath}/staff_invitations`);
    const unsubInv = onSnapshot(invRef, (snapshot) => {
      const invList = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setInvitations(invList);
    }, (err) => {
      console.warn('[useTeamMembers] Error escuchando invitaciones:', err);
    });

    return () => unsubInv();
  }, [user, targetTeamId, getTeamPath]);

  // 3. Determinar el rol del usuario actual en el equipo
  const currentUserRole = useMemo(() => {
    if (!user) return 'admin';
    const member = members.find(m => m.uid === user.uid || m.id === user.uid);
    if (member) return normalizeRole(member.role);
    if (currentTeam?.ownerUid === user.uid) return 'admin';
    return 'admin'; // default si es el creador
  }, [user, members, currentTeam]);

  // Permisos granulares según el rol
  const permissions = useMemo(() => {
    const role = currentUserRole;
    const isOwnerOrAdmin = ['admin', 'first_coach', 'owner'].includes(role);
    const isSeniorCoach = isOwnerOrAdmin || ['coach', 'second_coach'].includes(role);

    return {
      role,
      roleInfo: getRoleInfo(role),
      isAdmin: isOwnerOrAdmin,
      isFirstCoach: isOwnerOrAdmin,
      isCoach: isSeniorCoach,
      canManageStaff: true, // Todos los miembros del cuerpo técnico pueden añadir/invitar colaboradores
      canDeleteTeam: isOwnerOrAdmin,
      canEditMatches: isSeniorCoach || ['analyst', 'assistant', 'assistant_coach', 'fitness_coach'].includes(role),
      canEditLineups: isSeniorCoach || role === 'fitness_coach',
      canLiveRecord: isSeniorCoach || ['analyst', 'assistant', 'assistant_coach', 'fitness_coach'].includes(role),
      canEditSessions: isSeniorCoach || role === 'fitness_coach',
      canEditTacticBoard: isSeniorCoach || role === 'analyst' || role === 'fitness_coach',
      canEditPlayers: isSeniorCoach || role === 'fitness_coach',
      canEditHealth: isOwnerOrAdmin || ['physio', 'fitness_coach'].includes(role),
      canViewHealth: isSeniorCoach || ['physio', 'fitness_coach'].includes(role),
      canTakeAttendance: isSeniorCoach || ['assistant', 'assistant_coach', 'physio', 'fitness_coach'].includes(role),
      canExportReports: isSeniorCoach || ['analyst', 'fitness_coach'].includes(role),
      isPhysioOnly: role === 'physio',
      isFitnessCoach: role === 'fitness_coach',
      isPlayer: role === 'player',
      isReadOnly: role === 'player'
    };
  }, [currentUserRole]);

  // 4. Crear invitación para un nuevo miembro del cuerpo técnico
  const inviteMember = useCallback(async (email = '', role = 'coach') => {
    if (!user || !targetTeamId) return null;
    const emailClean = (email || '').trim().toLowerCase();

    // Comprobar si ya es miembro SOLO si se introdujo un email específico
    if (emailClean && members.some(m => m.email?.toLowerCase() === emailClean)) {
      showToast(t('teamMembers.already_member'), 'warning');
      return null;
    }

    const randomSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
    const inviteCode = randomSuffix;
    const token = `staff_${Date.now()}_${inviteCode.toLowerCase()}`;
    const teamPath = getTeamPath(targetTeamId);

    const inviteData = {
      id: token,
      token,
      inviteCode,
      teamId: targetTeamId,
      teamPath,
      teamName: currentTeam?.nombre || currentTeam?.name || 'Mi Equipo',
      teamCategory: currentTeam?.categoria || currentTeam?.category || 'General',
      invitedByUid: user.uid,
      invitedByName: user.displayName || user.email,
      email: emailClean || null,
      role: normalizeRole(role),
      status: 'pending',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 días
    };

    // Guardar en subcolección del equipo y en colección global de invitaciones de staff
    try {
      await setDoc(doc(db, `${teamPath}/staff_invitations`, token), inviteData);
      await setDoc(doc(db, 'staff_invitations', token), inviteData);
      await setDoc(doc(db, 'staff_invitations', inviteCode), inviteData);

      // Sincronizar en staff_codes para validación instantánea
      const staffCodePayload = {
        teamId: targetTeamId,
        teamPath,
        teamName: currentTeam?.nombre || currentTeam?.name || 'Mi Equipo',
        ownerUid: user.uid,
        rawCode: inviteCode,
        role: normalizeRole(role),
        createdAt: serverTimestamp(),
        active: true
      };
      await setDoc(doc(db, 'staff_codes', inviteCode), { ...staffCodePayload, code: inviteCode }, { merge: true });
      await setDoc(doc(db, 'staff_codes', `STAFF-${inviteCode}`), { ...staffCodePayload, code: `STAFF-${inviteCode}` }, { merge: true });
    } catch (e) {
      console.warn('[useTeamMembers] Error guardando invitación en Firestore:', e);
    }

    const inviteUrl = `${window.location.origin}/join-team/${token}`;
    showToast(t('teamMembers.invite_generated'), 'success');
    return { ...inviteData, inviteUrl, inviteCode };
  }, [user, targetTeamId, getTeamPath, currentTeam, members]);

  // 5. Actualizar rol de un miembro o cambiar propio rol
  const updateMemberRole = useCallback(async (memberUid, newRole) => {
    if (!user || !targetTeamId) return;

    const normRole = normalizeRole(newRole);
    const teamPath = getTeamPath(targetTeamId);
    const memberRef = doc(db, `${teamPath}/members`, memberUid);
    
    // Comprobar o crear doc si no existía previamente
    try {
      await setDoc(memberRef, {
        uid: memberUid,
        email: members.find(m => m.uid === memberUid)?.email || user.email,
        displayName: members.find(m => m.uid === memberUid)?.displayName || user.displayName || 'Entrenador',
        role: normRole,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (e) {
      console.warn('[updateMemberRole] error setDoc member:', e);
    }

    // Actualizar también en el array `members` del documento del equipo para consultas rápidas
    try {
      const teamRef = doc(db, teamPath);
      const teamSnap = await getDoc(teamRef);
      if (teamSnap.exists()) {
        const currentList = teamSnap.data().members || [];
        const existsInList = currentList.some(m => m.uid === memberUid);
        const updatedList = existsInList
          ? currentList.map(m => m.uid === memberUid ? { ...m, role: normRole } : m)
          : [...currentList, { uid: memberUid, email: user.email, displayName: user.displayName || 'Entrenador', role: normRole }];
        await updateDoc(teamRef, { members: updatedList }).catch(() => {});
      }
    } catch (e) {}

    // Actualizar puntero en el documento shared_teams del usuario
    try {
      const sharedRef = doc(db, 'users', memberUid, 'shared_teams', targetTeamId);
      await setDoc(sharedRef, {
        id: targetTeamId,
        teamId: targetTeamId,
        teamPath,
        teamName: currentTeam?.nombre || currentTeam?.name || 'Mi Equipo',
        role: normRole,
        updatedAt: serverTimestamp()
      }, { merge: true }).catch(() => {});
    } catch (e) {}

    showToast(t('teamMembers.role_updated', { role: getRoleInfo(normRole).label }), 'success');
  }, [user, targetTeamId, getTeamPath, members, currentTeam]);

  // 6. Cambiar mi propio rol rápidamente
  const switchMyRole = useCallback(async (newRole) => {
    if (!user) return;
    await updateMemberRole(user.uid, newRole);
  }, [user, updateMemberRole]);

  // 7. Eliminar a un miembro del cuerpo técnico
  const removeMember = useCallback(async (memberUid) => {
    if (!user || !targetTeamId) return;
    if (!permissions.isFirstCoach && memberUid !== user.uid) {
      showToast(t('teamMembers.admin_only_remove'), 'error');
      return;
    }

    const teamPath = getTeamPath(targetTeamId);
    await deleteDoc(doc(db, `${teamPath}/members`, memberUid));

    // Eliminar del array `members` del documento del equipo
    const teamRef = doc(db, teamPath);
    const teamSnap = await getDoc(teamRef);
    if (teamSnap.exists()) {
      const currentList = teamSnap.data().members || [];
      const updatedList = currentList.filter(m => m.uid !== memberUid);
      await updateDoc(teamRef, { members: updatedList }).catch(() => {});
    }

    // Eliminar de shared_teams del usuario
    try {
      await deleteDoc(doc(db, 'users', memberUid, 'shared_teams', targetTeamId)).catch(() => {});
    } catch (e) {}

    showToast(t('teamMembers.member_removed'), 'info');
  }, [user, targetTeamId, getTeamPath, permissions.isFirstCoach]);

  // 8. Cancelar invitación pendiente
  const cancelInvitation = useCallback(async (token) => {
    if (!targetTeamId) return;
    const teamPath = getTeamPath(targetTeamId);
    await deleteDoc(doc(db, `${teamPath}/staff_invitations`, token)).catch(() => {});
    await deleteDoc(doc(db, 'staff_invitations', token)).catch(() => {});
    showToast(t('teamMembers.invite_cancelled'), 'info');
  }, [targetTeamId, getTeamPath]);

  return {
    members,
    loading,
    invitations,
    currentUserRole,
    permissions,
    inviteMember,
    updateMemberRole,
    switchMyRole,
    removeMember,
    cancelInvitation,
    STAFF_ROLES,
    getRoleInfo,
    normalizeRole
  };
};
